import {FC, RefObject, useState} from 'react';
import {
  api,
  AGENT_TASK_EDITABLE_STATUSES,
  AGENT_TASK_TERMINAL_STATUSES,
  EAgentTaskStatus,
  getDateTimeString,
  IAgentTask,
  useT
} from '@kcast-fe/core';
import {Badge, ConfirmDialog, IQuickMenuItem, QuickMenu, Svg, TBadgeVariant} from 'ui-kit';
import {Box, Flex} from '@chakra-ui/react';

interface IProps {
  task: IAgentTask;
  containerRef: RefObject<HTMLDivElement | null>;
  onEdit: (task: IAgentTask) => void;
}

const STATUS_VARIANT: Record<EAgentTaskStatus, TBadgeVariant> = {
  [EAgentTaskStatus.Pending]: 'neutral',
  [EAgentTaskStatus.InProgress]: 'info',
  [EAgentTaskStatus.Accepted]: 'info',
  [EAgentTaskStatus.Dispatched]: 'info',
  [EAgentTaskStatus.Running]: 'info',
  [EAgentTaskStatus.Delayed]: 'neutral',
  [EAgentTaskStatus.Completed]: 'success',
  [EAgentTaskStatus.Failed]: 'error',
  [EAgentTaskStatus.Cancelled]: 'error',
  [EAgentTaskStatus.Missing]: 'error'
};

const AgentTaskItemFC: FC<IProps> = ({task, containerRef, onEdit}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deleteMutation = api.agentTasks.useDeleteAgentTaskMutation();

  const {t} = useT();

  const isEditable = AGENT_TASK_EDITABLE_STATUSES.includes(task.status);
  const isTerminal = AGENT_TASK_TERMINAL_STATUSES.includes(task.status);

  const handleDelete = () => {
    deleteMutation.mutate(task.id, {onSuccess: () => setConfirmDelete(false)});
  };

  const menuItems: IQuickMenuItem<string>[] = [];
  if (isEditable) {
    menuItems.push({
      id: 'edit',
      name: t('agentTasks.actions.edit'),
      onClick: () => onEdit(task)
    });
  }
  if (!isTerminal) {
    menuItems.push({
      id: 'cancel',
      name: t('agentTasks.actions.cancelTask'),
      isDanger: true,
      onClick: () => setConfirmDelete(true)
    });
  }

  return (
    <>
      <Flex
        align={'flex-start'}
        gap={'8px'}
        p={'16px 0 12px 4px'}
        borderBottom={'thin solid'}
        borderColor={'{colors.borderDefault}'}
        className={'group'}
        _hover={{bg: '{colors.bgSurface}'}}
        transition={'background var(--tr-100-ei)'}
      >
        <Flex direction={'column'} gap={'6px'} flex={'1 1 auto'} minW={0} pl={'4px'}>
          <Flex gap={'8px'} alignItems={'center'} fontSize={'14px'}>
            <Badge variant={STATUS_VARIANT[task.status]}>
              {t(`agentTasks.status.${task.status}`)}
            </Badge>
            {task.agent_id && <Box color={'{colors.textTertiary}'}>{task.agent_id}</Box>}
          </Flex>

          <Box color={'{colors.textPrimary}'}>{task.prompt}</Box>

          <Flex fontSize={'14px'} gap={'4px'} color={'{colors.textTertiary}'}>
            <Box fontWeight={600}>Created:</Box>
            <Box>{getDateTimeString(task.created_at)}</Box>
            {task.created_at !== task.updated_at && (
              <>
                <Box fontWeight={600} pl={'4px'}>
                  Updated:
                </Box>
                <Box>{getDateTimeString(task.updated_at)}</Box>
              </>
            )}
          </Flex>
        </Flex>

        {menuItems.length > 0 && (
          <Flex
            pr={'16px'}
            opacity={0}
            _groupHover={{opacity: 1}}
            transition={'opacity var(--tr-150-ei)'}
          >
            <QuickMenu
              hideActive
              container={containerRef}
              trigger={<Svg name={'Dots'} size={'s24'} />}
              items={menuItems}
            />
          </Flex>
        )}
      </Flex>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('agentTasks.confirm.deleteTitle')}
        body={t('agentTasks.confirm.deleteBody')}
        confirmLabel={t('agentTasks.actions.cancelTask')}
        variant={'danger'}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export const AgentTaskItem = AgentTaskItemFC;
